/**
 * A sign-in page whose form has no more reason to be (redesign §37.6, rule 26):
 * the reset link is sent, or the link that opened the page is no longer valid.
 * The column keeps its display title and subtitle, then at most one value in
 * mono (the address), one action — primary when it leads on, secondary when it
 * repeats — and the foot « Back to sign in ». No coloured note: the title says
 * the state.
 */
import { useEffect, useRef, useState } from 'react';
import type React from 'react';
import { color, font, radius } from '../theme/tokens';
import { CheckMark } from '../components/icons';
import { PrimaryButton } from '../components/primitives';
import { api } from '../lib/api';

export type AuthState = { kind: 'sent'; email: string } | { kind: 'invalid' };

const TITLE: React.CSSProperties = { fontSize: 48, lineHeight: 1.04, letterSpacing: '-.03em', fontWeight: 700, margin: '0 0 10px' };

const LINK: React.CSSProperties = {
  background: 'none',
  border: 0,
  padding: 0,
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 13.5,
  color: color.accentText
};

/** « Send it again » (10e): the same request, then « Sent » with a check on the button for 1.6 s. */
function SendAgain({ email }: { email: string }): React.JSX.Element {
  const [phase, setPhase] = useState<'idle' | 'sending' | 'sent'>('idle');
  const timer = useRef(0);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const again = (): void => {
    if (phase !== 'idle') return;
    setPhase('sending');
    void api
      .post<{ ok: boolean }>('/auth/forgot', { email })
      .then(() => {
        setPhase('sent');
        timer.current = window.setTimeout(() => setPhase('idle'), 1600);
      })
      .catch(() => setPhase('idle'));
  };
  return (
    <button
      type="button"
      onClick={again}
      className="hdr-btn"
      data-press=""
      aria-busy={phase === 'sending' || undefined}
      style={{
        width: '100%',
        height: 36,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        background: color.surface,
        border: `1px solid ${color.lineStrong}`,
        borderRadius: radius.chip,
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: 14,
        color: color.ink
      }}
    >
      <span aria-live="polite" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        {phase === 'sent' && <CheckMark size={15} />}
        {phase === 'sent' ? 'Sent' : phase === 'sending' ? 'Sending' : 'Send it again'}
      </span>
    </button>
  );
}

export function AuthStatePage({
  state,
  siteUrl,
  onBack,
  onNewLink
}: {
  state: AuthState;
  siteUrl: string;
  onBack: () => void;
  /** « Get new link »: the forgot-password form, to ask again. */
  onNewLink: () => void;
}): React.JSX.Element {
  const site = siteUrl.replace(/\/+$/, '');
  return (
    <div style={{ width: '100%' }}>
      <h1 className="al-auth-title" style={TITLE}>
        {state.kind === 'sent' ? 'Check your inbox' : 'Reset link no longer valid'}
      </h1>
      <p style={{ fontSize: 14.5, lineHeight: 1.55, color: color.inkBody, margin: '0 0 28px' }}>
        {state.kind === 'sent'
          ? 'If that address has an account, a link to set a new password is on its way. It is good for one hour.'
          : 'Ask for a new one. It is good for one hour, and works once.'}
      </p>
      {state.kind === 'sent' && (
        <p style={{ fontFamily: font.mono, fontSize: 13.5, color: color.ink, margin: '0 0 20px', wordBreak: 'break-all' }}>{state.email}</p>
      )}
      {state.kind === 'sent' ? (
        <SendAgain email={state.email} />
      ) : (
        <PrimaryButton full size="lg" onClick={onNewLink}>
          Get new link
        </PrimaryButton>
      )}
      <div style={{ marginTop: 18 }}>
        <button type="button" onClick={onBack} style={LINK}>
          Back to sign in
        </button>
      </div>
      <p style={{ fontSize: 12.5, color: color.inkFaint, lineHeight: 1.5, margin: '16px 0 0' }}>
        By continuing you accept the <a href={`${site}/legal/cgv`}>Terms of sale &amp; use</a> and the{' '}
        <a href={`${site}/legal/privacy`}>privacy policy</a>.
      </p>
    </div>
  );
}
